import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Plus, Trash2, UserCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { UnitPicker } from '@/features/candidacy/UnitPicker'
import {
  ballotFor, submitReturn,
  type AgentEntry, type BallotEntry, type Figures,
} from '../api'
import { formLabel, seatLabel } from '../vocab'
import { DateTimePicker } from '@/components/erp/date-picker'

/**
 * Key a statutory results form in by hand.
 *
 * ## The same destination as the scanner, by a different road
 *
 * This produces exactly what the OCR wizard produces — a `tally_return` at `SUBMITTED`,
 * which then climbs the same ladder of verify → certify and is corrected the same way. The
 * only difference the system records is `sourceCode`: `AGENT_ENTRY` rather than `FORM_OCR`,
 * which is the first question asked when a figure turns out to be wrong. Nothing here is a
 * lesser kind of return.
 *
 * ## Why it is one screen for every form
 *
 * 34A, 35A, 36A, 37A, 38A, 39A and the B/C sheets differ in exactly two things: which seat
 * they are for, and therefore which names are on the ballot. Both come from the server —
 * `ballotFor` answers the second — so a per-form screen would be six copies of this one
 * drifting apart. Add a seat to the register and this screen already handles it.
 *
 * ## The place is picked at the FORM's level, not the seat's
 *
 * A presidential candidacy sits at COUNTRY, but presidential votes are counted at a POLLING
 * STATION and Form 34A is the polling-station form. Narrowing the picker to the seat's level
 * — as the upload step does — invites a 34A filed against the whole republic, whose register
 * of voters is not a number that station-level turnout means anything against.
 */
export function ManualEntry({
  open, electionId, onClose, onDone,
}: {
  open: boolean
  electionId: string
  onClose: () => void
  onDone: () => void | Promise<void>
}) {
  const [seatType, setSeatType] = useState('PRESIDENT')
  const [formType, setFormType] = useState('FORM_34A')
  const [unitId, setUnitId] = useState<string | null>(null)

  const [ballot, setBallot] = useState<BallotEntry[]>([])
  const [loadingBallot, setLoadingBallot] = useState(false)

  const [registered, setRegistered] = useState('')
  const [rejected, setRejected] = useState('')
  const [spoilt, setSpoilt] = useState('')
  const [disputed, setDisputed] = useState('')
  const [votes, setVotes] = useState<Record<string, string>>({})

  const [presiding, setPresiding] = useState('')
  const [deputy, setDeputy] = useState('')
  const [declaredAt, setDeclaredAt] = useState('')
  const [agents, setAgents] = useState<AgentRow[]>([])

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setSeatType('PRESIDENT'); setFormType('FORM_34A'); setUnitId(null)
    setBallot([]); setVotes({})
    setRegistered(''); setRejected(''); setSpoilt(''); setDisputed('')
    setPresiding(''); setDeputy(''); setDeclaredAt(''); setAgents([])
    setError(null); setBusy(false)
  }, [open])

  /*
     The STATION's id is what goes over the wire, and the server walks up from it to the unit
     that carries the ballot — candidacies are registered where the seat is contested, so a
     presidential ballot lives at COUNTRY while the form is filled in at a polling station.
     Resolving that here instead would put a copy of SeatTypeRules in the browser.

     electionId is in the dependency array because every election-scoped loader needs it: an
     empty array binds to the null seen on first render and the screen stays permanently
     empty with nothing saying why.
  */
  const loadBallot = useCallback(async () => {
    if (!electionId || !unitId) { setBallot([]); return }
    setLoadingBallot(true)
    try {
      setError(null)
      setBallot(await ballotFor({ electionId, seatType, unitId }))
    } catch (err) {
      setError(explain(err))
      setBallot([])
    } finally {
      setLoadingBallot(false)
    }
  }, [electionId, seatType, unitId])

  useEffect(() => { void loadBallot() }, [loadBallot])

  const num = (s: string) => {
    const n = Number.parseInt(s, 10)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }

  // The two derived figures the server computes and stores once. Shown, never sent — the
  // request body has no field for either, deliberately, so there is one writer for each.
  const validVotes = useMemo(
    () => ballot.reduce((sum, b) => sum + num(votes[b.id] ?? ''), 0),
    [ballot, votes],
  )
  const totalCast = validVotes + num(rejected) + num(spoilt)
  const registeredNum = num(registered)
  const turnout = registeredNum > 0 ? (totalCast / registeredNum) * 100 : null
  const overSubscribed = registeredNum > 0 && totalCast > registeredNum

  const ready = unitId !== null && ballot.length > 0 && !busy

  const submit = async () => {
    if (!unitId) return
    setBusy(true)
    try {
      setError(null)
      const figures: Figures = {
        registeredVoters: registeredNum,
        validVotes,
        rejectedVotes: num(rejected),
        disputedVotes: num(disputed),
        spoiltVotes: num(spoilt),
        candidates: ballot.map((b) => ({ candidacyId: b.id, votes: num(votes[b.id] ?? '') })),
      }
      /*
         The declaration is OMITTED when nothing in it was keyed, never sent empty. An empty
         agents array is a statement — "the form listed nobody" — and would clear a witness
         list; absence means "not supplied". The two are different requests on purpose.
      */
      const cleanAgents: AgentEntry[] = agents
        .filter((a) => a.name.trim() !== '')
        .map((a) => ({
          name: a.name.trim(),
          candidacyId: a.candidacyId || null,
          represents: a.represents.trim() || null,
          signed: a.signed,
          refusalReason: a.signed ? null : (a.refusalReason.trim() || null),
        }))
      const touched = presiding.trim() !== '' || deputy.trim() !== ''
        || declaredAt !== '' || cleanAgents.length > 0

      await submitReturn({
        electionId,
        seatTypeCode: seatType,
        unitId,
        formTypeCode: formType,
        figures,
        ...(touched ? {
          declaration: {
            presidingOfficerName: presiding.trim() || null,
            deputyOfficerName: deputy.trim() || null,
            declaredAt: declaredAt ? new Date(declaredAt).toISOString() : null,
            agents: cleanAgents,
          },
        } : {}),
      })
      await onDone()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Key in a results form" width="lg">
      <div className="space-y-5">
        <ErrorStrip message={error} className="rounded-md border" />

        {/* ── the race ──────────────────────────────────────────────────── */}
        <section className="space-y-2">
          <h3 className="text-erp-sm font-semibold">Which form</h3>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">Race</span>
              <select value={seatType} className="erp-input h-9 w-full text-erp-sm"
                onChange={(e) => {
                  setSeatType(e.target.value)
                  setFormType(stationFormFor(e.target.value))
                  setUnitId(null)
                  setVotes({})
                }}>
                {SEATS.map((s) => <option key={s} value={s}>{seatLabel(s)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">Form</span>
              <select value={formType} className="erp-input h-9 w-full text-erp-sm"
                onChange={(e) => { setFormType(e.target.value); setUnitId(null) }}>
                {FORM_TYPES.map((f) => <option key={f} value={f}>{formLabel(f)}</option>)}
              </select>
            </label>
            <div>
              <span className="block text-erp-xs text-muted-foreground mb-1">
                Where it was counted
              </span>
              <UnitPicker level={levelForForm(formType)} value={unitId} onPick={setUnitId} />
            </div>
          </div>
          {!unitId && (
            <Note>
              Choose the place first. {formLabel(formType)} is filled in at
              {' '}{(levelForForm(formType) ?? 'any level').toLowerCase().replace('_', ' ')} level,
              and the names on the ballot are loaded from it.
            </Note>
          )}
        </section>

        {/* ── the candidates ────────────────────────────────────────────── */}
        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-erp-sm font-semibold">Votes cast for each candidate</h3>
            {loadingBallot && (
              <span className="text-erp-xs text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> loading the ballot
              </span>
            )}
          </div>

          {unitId && !loadingBallot && ballot.length === 0 && (
            <Note>
              Nobody is nominated for this race at this place, so there is no ballot to key
              figures against. Register the candidacies first — a vote recorded against
              nobody is a vote attributed to nobody.
            </Note>
          )}

          {ballot.length > 0 && (
            <div className="rounded-md border border-border divide-y divide-border">
              {ballot.map((b, i) => (
                <div key={b.id} className="flex items-center gap-3 px-3 py-2">
                  <span className="text-erp-xs text-muted-foreground w-5 tabular-nums">
                    {b.ballotNumber ?? i + 1}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-erp-sm truncate">{b.ballotName}</span>
                    <span className="block text-erp-xs text-muted-foreground truncate">
                      {b.independent ? 'Independent' : (b.partyShortName ?? b.partyName ?? '—')}
                    </span>
                  </span>
                  <input inputMode="numeric" value={votes[b.id] ?? ''}
                    onChange={(e) => setVotes((v) => ({ ...v, [b.id]: digits(e.target.value) }))}
                    className="erp-input h-9 w-28 text-erp-sm text-right tabular-nums"
                    placeholder="0" aria-label={`Votes for ${b.ballotName}`} />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── the statistics ────────────────────────────────────────────── */}
        <section className="space-y-2">
          <h3 className="text-erp-sm font-semibold">Polling station statistics</h3>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <NumberField label="Registered voters" value={registered} onChange={setRegistered} />
            <NumberField label="Rejected ballots" value={rejected} onChange={setRejected} />
            <NumberField label="Spoilt ballots" value={spoilt} onChange={setSpoilt} />
            <NumberField label="Disputed votes" value={disputed} onChange={setDisputed} />
          </div>

          {/*
              Valid votes and total cast are NOT inputs. Both are derived — valid is the sum
              of the names above, total is valid + rejected + spoilt — and the server computes
              and stores them itself. An input here would be a second writer for a number that
              already has one, and the two would eventually disagree.
          */}
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            <Derived label="Valid votes" value={validVotes} hint="sum of the names above" />
            <Derived label="Total cast" value={totalCast} hint="valid + rejected + spoilt" />
            <Derived label="Turnout"
              value={turnout === null ? '—' : `${turnout.toFixed(2)}%`}
              hint={registeredNum > 0 ? 'of the register' : 'needs a register'} />
          </div>

          {overSubscribed && (
            <Note tone="bad">
              More ballots cast ({totalCast.toLocaleString()}) than registered voters
              ({registeredNum.toLocaleString()}). This is recorded, not refused — it is
              occasionally the thing an entire petition is about — and it will be waiting at
              the reconciliation desk.
            </Note>
          )}
        </section>

        {/* ── the declaration ───────────────────────────────────────────── */}
        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-erp-sm font-semibold">Declaration</h3>
            <span className="text-erp-xs text-muted-foreground">optional</span>
          </div>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">
                Presiding officer
              </span>
              <input value={presiding} onChange={(e) => setPresiding(e.target.value)}
                maxLength={200} className="erp-input h-9 w-full text-erp-sm"
                placeholder="As written on the form" />
            </label>
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">
                Deputy presiding officer
              </span>
              <input value={deputy} onChange={(e) => setDeputy(e.target.value)}
                maxLength={200} className="erp-input h-9 w-full text-erp-sm" />
            </label>
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">
                Declared at (on the paper)
              </span>
              <DateTimePicker
                value={declaredAt}
                onChange={setDeclaredAt}
                className="h-9 w-full text-erp-sm"
              />
            </label>
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-erp-xs text-muted-foreground inline-flex items-center gap-1.5">
              <UserCheck className="h-3.5 w-3.5" />
              Agents present at the declaration
            </span>
            <button type="button" onClick={() => setAgents((a) => [...a, blankAgent()])}
              className="erp-btn h-8 px-2 text-erp-xs inline-flex items-center gap-1.5">
              <Plus className="h-3 w-3" /> Add an agent
            </button>
          </div>

          {agents.length > 0 && (
            <div className="space-y-2">
              {agents.map((a, i) => (
                <div key={a.key} className="rounded-md border border-border p-2 space-y-2">
                  <div className="grid gap-2"
                    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                    <input value={a.name} maxLength={200}
                      onChange={(e) => patchAgent(setAgents, i, { name: e.target.value })}
                      className="erp-input h-9 w-full text-erp-sm" placeholder="Agent's name" />
                    <select value={a.candidacyId}
                      onChange={(e) => {
                        const id = e.target.value
                        const b = ballot.find((x) => x.id === id)
                        patchAgent(setAgents, i, {
                          candidacyId: id,
                          // Prefill the verbatim column from the register, still editable —
                          // what the paper said is the evidence, and it may not match.
                          represents: b
                            ? (b.independent ? 'Independent'
                              : (b.partyShortName ?? b.partyName ?? b.ballotName))
                            : a.represents,
                        })
                      }}
                      className="erp-input h-9 w-full text-erp-sm">
                      <option value="">Represents (not on this ballot)</option>
                      {ballot.map((b) => (
                        <option key={b.id} value={b.id}>{b.ballotName}</option>
                      ))}
                    </select>
                    <input value={a.represents} maxLength={200}
                      onChange={(e) => patchAgent(setAgents, i, { represents: e.target.value })}
                      className="erp-input h-9 w-full text-erp-sm"
                      placeholder="As written on the form" />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-erp-xs">
                      <input type="checkbox" checked={a.signed}
                        onChange={(e) => patchAgent(setAgents, i, {
                          signed: e.target.checked,
                          refusalReason: e.target.checked ? '' : a.refusalReason,
                        })} />
                      Signed
                    </label>
                    {!a.signed && (
                      <input value={a.refusalReason} maxLength={2000}
                        onChange={(e) => patchAgent(setAgents, i,
                          { refusalReason: e.target.value })}
                        className="erp-input h-8 flex-1 min-w-[12rem] text-erp-xs"
                        placeholder="Reason for refusing to sign, if one was given" />
                    )}
                    <button type="button"
                      onClick={() => setAgents((rows) => rows.filter((_, j) => j !== i))}
                      className="erp-btn h-8 px-2 text-erp-xs inline-flex items-center gap-1.5
                                 ml-auto text-muted-foreground">
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </div>
                </div>
              ))}
              <p className="text-erp-xs text-muted-foreground">
                An agent refusing to sign does not invalidate the result — Regulation 79(7).
                It is recorded here with its reason, and the return is filed either way.
              </p>
            </div>
          )}
        </section>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="erp-btn h-9 px-3 text-erp-sm">
            Cancel
          </button>
          <button type="button" disabled={!ready} onClick={() => void submit()}
            className={cn('erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2',
              !ready && 'opacity-50 cursor-not-allowed')}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            File this return
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ── bits ─────────────────────────────────────────────────────────────────────────────────

interface AgentRow {
  key: string
  name: string
  candidacyId: string
  represents: string
  signed: boolean
  refusalReason: string
}

let agentSeq = 0
const blankAgent = (): AgentRow => ({
  key: `agent-${agentSeq++}`,
  name: '', candidacyId: '', represents: '', signed: true, refusalReason: '',
})

function patchAgent(
  set: React.Dispatch<React.SetStateAction<AgentRow[]>>, i: number, patch: Partial<AgentRow>,
) {
  set((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
}

/** Digits only. A vote count is a whole number of people and nothing else. */
const digits = (s: string) => s.replace(/[^0-9]/g, '')

function NumberField({ label, value, onChange }: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="block text-erp-xs text-muted-foreground mb-1">{label}</span>
      <input inputMode="numeric" value={value} placeholder="0"
        onChange={(e) => onChange(digits(e.target.value))}
        className="erp-input h-9 w-full text-erp-sm text-right tabular-nums" />
    </label>
  )
}

function Derived({ label, value, hint }: {
  label: string
  value: number | string
  hint: string
}) {
  return (
    <div>
      <span className="block text-erp-xs text-muted-foreground">{label}</span>
      <span className="block text-erp-base font-semibold tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
      <span className="block text-erp-xs text-muted-foreground">{hint}</span>
    </div>
  )
}

const SEATS = ['PRESIDENT', 'GOVERNOR', 'SENATOR', 'WOMAN_REP', 'MP', 'MCA']

const FORM_TYPES = [
  'FORM_34A', 'FORM_34B', 'FORM_34C', 'FORM_35A', 'FORM_35B', 'FORM_36A', 'FORM_36B',
  'FORM_37A', 'FORM_37B', 'FORM_38A', 'FORM_38B', 'FORM_39A', 'FORM_39B', 'OTHER',
]

const stationFormFor = (seat: string) => ({
  PRESIDENT: 'FORM_34A', GOVERNOR: 'FORM_35A', SENATOR: 'FORM_36A',
  WOMAN_REP: 'FORM_37A', MP: 'FORM_38A', MCA: 'FORM_39A',
}[seat] ?? 'OTHER')

/**
 * The geography level a FORM is filled in at — which is not the level its seat is contested
 * at. The suffix is the level and it is consistent across the set: A is the polling station,
 * B is the constituency or county tally of those, C is national.
 */
const levelForForm = (formType: string): string | null => {
  if (formType === 'OTHER') return null
  if (formType.endsWith('A')) return 'POLLING_STATION'
  if (formType.endsWith('C')) return 'COUNTRY'
  return {
    FORM_34B: 'CONSTITUENCY', FORM_35B: 'COUNTY', FORM_36B: 'COUNTY',
    FORM_37B: 'COUNTY', FORM_38B: 'CONSTITUENCY', FORM_39B: 'WARD',
  }[formType] ?? null
}
